import { computed, signal, Signal, useSignal } from "@preact/signals-react";
import { BrainClient as CorekitBrainClient } from "imagica-corekit/dist/base/api/BrainClient";
import { MeStore } from "imagica-corekit/dist/base/store/MeStore";
import { getIt } from "@uikit/getIt";
import { CustomApiService } from "imagica-corekit/dist/cases/customApi/CustomApiService";
import { AuthStore } from "imagica-corekit/dist/base/store/AuthStore";
import { GlobalResponse } from "imagica-corekit/dist/base/cutil/GlobalClient";
import { CustomApiProps } from "imagica-corekit/dist/base/storyV2/adapter/CustomApiAdapter";
import { FuncCustomVariable, FuncRunIdentityInfo } from "imagica-corekit/dist/base/storyV2/function/FunctionBase";
import { tryPromise } from "imagica-corekit/dist/base/cutil/LangUtil";
import { GenUtil } from "imagica-corekit/dist/base/cutil/GenUtil";
import { Header } from "@uiview/pages/customApi/HeaderList";
import func from "@uikit/func";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { JsonUtil } from "imagica-corekit/dist/base/cutil/JsonUtil";
import { CustomApiUtil } from "imagica-corekit/dist/cases/util/CustomApiUtil";
import { Type } from "class-transformer";

class ApiResponseData {
  type = "";
  value = {};
}
export class ApiResponse {
  size = "";
  status = 200;
  statusText = "";
  time = "";
  responseHeaders = {};
  @Type(() => ApiResponseData)
  data = new ApiResponseData();
}

export class RequestData {
  url = "";
  method = "get";
  headerList: Header[] = [];
  bodyParam = "";
  copyWith(params: { url?: string; method?: string; headerList?: Header[]; bodyParam?: string }) {
    return GenUtil.copyWith(new RequestData(), this, params);
  }
}
export class CustomApiPageState {
  createAPIFuncOpen: Signal<boolean> = signal(false);
  apiLoading = signal(false);
  showPublishFunctionConfirm = signal(false);
  publishFunctionLoading = signal(false);
  isFuncLoading = signal(false);
  isNewCustomFunctionOpen = signal(false);
  selectBodyType = signal(false);
  selectedOption = signal("Json");
  childState = signal("");
  customFunctionTitle = signal("");
  functionId: Signal<string | null> = signal("");
  newFunctionData = signal({ name: "", description: "" });

  response: Signal<ApiResponse | null> = signal(new ApiResponse());
  requestData = signal(new RequestData());
  responseHeaderList = computed<{ key: string; name: string; value: string }[]>(() => {
    const responseHeaders = this.response.value?.responseHeaders as any;
    if (responseHeaders && !func.isEmpty(responseHeaders)) {
      const headerLists = Object.keys(responseHeaders).map((x, index) => {
        return {
          key: `${index}_${x.toLowerCase()}`,
          name: x,
          value: responseHeaders[x],
        };
      });
      return headerLists;
    }
    return [];
  });
}

export class CustomApiPageBloc {
  brainClient = getIt(CorekitBrainClient);
  meStore = getIt(MeStore);
  apiService = new CustomApiService(getIt(AuthStore), getIt(CorekitBrainClient));
  getSourceNodeValue: () => string = () => {
    return "";
  };
  getVarliableList: () => FuncCustomVariable[] = () => {
    return [];
  };
  getEvaluateFinalBodyStr(newBodyParam: string, sourceNodeValue: string, variableList: FuncCustomVariable[]) {
    if (func.isEmpty(newBodyParam)) {
      return "";
    }
    // 注入currentSourceNodeValue
    return CustomApiUtil.evaluateFinalBodyStr(sourceNodeValue, newBodyParam, variableList);
  }

  evaluateFinalBodyStr = computed(() => {
    const requestData = this.state.requestData.value;
    return this.getEvaluateFinalBodyStr(requestData.bodyParam, this.getSourceNodeValue(), this.getVarliableList());
  });
  state = new CustomApiPageState();
  constructor(public enterPath: string) {
    this.initData();
  }
  async initData() {
    const regex = /\/function\/(?:\d+|Untitled)/;
    const result = regex.test(this.enterPath);
    if (result) {
      const hasPermission = await this.meStore.hasCustomApiV2();
      if (hasPermission) {
        this.switchPage(result);
      }
    }
  }

  get popProfilePath(): string {
    const parent = this.enterPath.split("/function").first();
    return parent ?? "";
  }

  switchPage(value: boolean): void {
    this.state.createAPIFuncOpen.value = value;
  }

  async sendRequest(apiData: CustomApiProps, input: string, variableList: FuncCustomVariable[]): Promise<void> {
    this.state.apiLoading.value = true;
    const start = Date.now();
    const tryResult = await tryPromise(this.apiService.sendRequest(apiData, input, variableList));
    this.state.apiLoading.value = false;
    if (tryResult.error) {
      console.log("error: ", tryResult, tryResult.error);
      return;
    }
    const response: any = tryResult.data;
    if (!response) {
      return;
    }
    const data = await this.handleResponseStatusData(response, start);
    this.state.response.value = JsonUtil.toModelFromType(ApiResponse, data) ?? new ApiResponse();
  }

  async getImageData(res: Response): Promise<string> {
    const s3Path = res.headers.get("X-Brain-S3-Path") || "";
    let value = "";

    if (!func.isEmpty(s3Path)) {
      value = `${settings.S3CDN}${encodeURI(s3Path)}`;
    } else {
      const blob = await res.blob();
      value = URL.createObjectURL(blob);
      URL.revokeObjectURL(value);
    }

    return value;
  }

  async decoderToText(res: any): Promise<string> {
    const reader = res.body?.getReader();

    if (!reader) {
      // Handle the case where the reader is null or undefined
      return "";
    }

    const result = await reader.read();
    const decoder = new TextDecoder();
    return decoder.decode(result.value);
  }

  humanizeBytes(bytes: number): string {
    if (bytes === 0) {
      return "0 Bytes";
    }

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size: number = parseFloat((bytes / Math.pow(k, i)).toFixed(2))!; // Non-null assertion

    return `${size} ${sizes[i]}`;
  }

  async handleResponseStatusData(globalResponse: GlobalResponse | null, start: number): Promise<null | ApiResponse> {
    if (!globalResponse) {
      return null;
    }
    const res = globalResponse.response;
    if (!res) return null;

    const end = Date.now();
    const diff = end - start;
    const size = res.headers.get("content-length");
    const contentType = res.headers.get("content-type");
    const responseHeaders = Object.fromEntries(res.headers.entries());
    let data: { type: string; value: any } = { type: "", value: "" };
    switch (true) {
      case contentType?.includes("image"):
        data.value = await this.getImageData(res);
        data.type = "image";
        break;
      case contentType?.includes("json"):
        try {
          data.value = await globalResponse.json();
          data.type = "json";
        } catch (_) {
          data.value = await globalResponse.text();
          data.type = "text";
        }
        break;
      case contentType?.includes("html"):
        data.value = await this.decoderToText(res.body);
        data.type = "html";
        break;
      default:
        data.value = await this.decoderToText(res.body);
        let finalRes = data.value;
        try {
          finalRes = JSON.parse(data.value);
        } catch (error) {}
        data = {
          type: "text",
          value: finalRes,
        };
    }
    return {
      status: Number(res.status),
      statusText: res.statusText,
      time: `${(diff / 1000).toFixed(2)} s`,
      size: this.humanizeBytes(Number(size)),
      data,
      responseHeaders,
    };
  }

  extractNumber(inputString: string): string | null {
    const startIndex = inputString.indexOf("function/") + "function/".length;
    const endIndex = inputString.length;

    if (startIndex >= "function/".length && endIndex > startIndex) {
      const extractedString = inputString.substring(startIndex, endIndex);

      // 增加检查，确保后面不是 "/Untitled"
      if (extractedString !== "Untitled") {
        return extractedString;
      }
    }

    return null;
  }

  setIsNewCustomFunctionOpen = (value: boolean): void => {
    this.state.isNewCustomFunctionOpen.value = value;
  };
  publishFunction = (value: boolean): void => {
    this.state.showPublishFunctionConfirm.value = value;
  };
  setCustomFunctionTitle = (value: string): void => {
    this.state.customFunctionTitle.value = value;
  };
  handleFunctionData = (): void => {
    this.setCustomFunctionTitle("");
    this.setIsNewCustomFunctionOpen(false);
  };
  setNewFunctionData = (data: Signal<{ name: string; description: string }> | any): void => {
    this.state.newFunctionData.value = data;
  };
  newFunctionDataChange = (e: React.ChangeEvent<HTMLInputElement>, key: string): void => {
    const data = Object.assign({}, { ...this.state.newFunctionData.value }, { [key]: e.target.value });
    this.setNewFunctionData(data);
  };
  setChildState = (value: string): void => {
    this.state.childState.value = value;
  };
  setSelectBodyType = (value: boolean): void => {
    this.state.selectBodyType.value = value;
  };
  setSelectedOption = (value: string): void => {
    this.state.selectedOption.value = value;
  };
  handleMenuClick = (option: string): void => {
    this.setSelectedOption(option);
    this.setSelectBodyType(false);
  };
}
